/**
 * MessageList — presentational message-list render region for the chat stream.
 *
 * Extracted verbatim from ChatBox.tsx as part of the god-file decomposition
 * (#3277, Phase 3 leaf extractions). Pure presentational component: every piece
 * of state, every ref, and every handler stays defined in ChatBox and is passed
 * in as a prop. No hooks live here — the returned JSX is a byte-for-byte move of
 * the `chat-messages-inner` subtree; only the surrounding identifiers became
 * props. The outer `chat-messages` div (and messagesContainerRef) remain in
 * ChatBox; this leaf renders the inner stream and owns the messagesEndRef anchor.
 *
 * CROSS-LAYER NOTE: this leaf is the new locus for the `@/modules` ModuleRenderer
 * default import — the documented quarantine exception relocating to where it is
 * actually used. features/ importing @/modules is the accepted exception here; no
 * dependency inversion is attempted.
 */
import { Fragment, useRef, useEffect } from 'react';
import type { RefObject, Dispatch, SetStateAction } from 'react';
import { HttpError } from '@/lib/http';
import { QuickActionChips } from '@/features/chat/components/QuickActionChips';
import { resolveConsentProvider } from '@/features/oauth/components/OAuthConsentModal';
import { connectCapabilityFor } from '@/features/oauth/lib/connect-capability';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { api } from '@/lib/api';
import { env } from '@/lib/env';
import { appendAuthToken } from '@/lib/url/appendAuthToken';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';
import type { Module } from '@/types/modules';
import type { QueryClient } from '@tanstack/react-query';
import ModuleRenderer from '@/modules/_infrastructure/ModuleRenderer';
import { AssistantMarkdown } from '@/common/components/AssistantMarkdown';
import { splitResultCards } from '@/features/chat/components/result-cards/types';
import { ResultCardRenderer } from '@/features/chat/components/result-cards/ResultCardRenderer';
import { VoiceOverlay } from '@/features/voice/VoiceOverlay';
import { ErrorBoundary } from '@/common/components/ErrorBoundary';
import { OnboardingActions } from './OnboardingActions.js';
import { parseBlocks } from '@/features/chat/utils/assistant-content';
import { RichText } from './chatbox/RichText';
import { MentionText } from './MentionText';
import { MENTION_MARKER_RE } from '@/features/chat/utils/mention-parser';
import { AnimatedThinkingLabel } from './chatbox/AnimatedThinkingLabel';
import { ThinkingTimeline } from './chatbox/ThinkingTimeline';
import { ChatInlineError } from './chatbox/ChatInlineError';
import { ModuleSelectionButtons } from './chatbox/ModuleSelectionButtons';
import { OnboardingSummaryCard, ONBOARDING_SUMMARY_MARKER } from './OnboardingSummaryCard';
import {
  OnboardingBeatCard,
  ONBOARDING_BEAT_MARKER,
  parseBeat,
  type BeatKind,
  type BeatPayload,
  type Bundle,
} from './OnboardingBeats';
import type { ChatMessage } from '@/features/chat/hooks/useChat';

// Props mirror the identifiers the subtree reads in ChatBox. State, refs, and
// handlers all stay defined in ChatBox and are passed down verbatim; structural
// values whose ChatBox types are complex (refs, module catalogs, the voiceMode /
// onboarding hook results) are typed loosely here so the call site keeps
// compiling without weakening behavior — this is a behavior-preserving JSX
// relocation, not a type-tightening change. The module-level singletons used by
// the subtree (env, api, apiPost, appendAuthToken, splitResultCards, parseBlocks,
// HttpError, resolveConsentProvider, ONBOARDING_SUMMARY_MARKER) are imported
// directly above rather than threaded through props.
export interface MessageListProps {
  voiceMode: any;
  messages: ChatMessage[];
  retryLastMessage: () => void | Promise<void>;
  dismissRetryBubble: () => void;
  activeConversation: { id: string } | null | undefined;
  sendMessage: (...args: any[]) => any;
  /** Run a slash command through the composer pipeline (parseInput) — NOT the
   *  raw sendMessage, which would post the literal command text to the agent. */
  runSlashCommand?: (cmd: string) => void;
  copiedMessageId: string | null;
  setCopiedMessageId: Dispatch<SetStateAction<string | null>>;
  clickableModuleLabels: any;
  openWorkspaceModuleByLabel: (label: string, messageId: string, occurrenceIndex: number) => void | Promise<void>;
  setObjectCardPopover: (v: { objectType: string; objectId: string } | null) => void;
  availableModulesRef: { current: Module[] };
  workspaceModuleCatalogRef: { current: WorkspaceModule[] };
  applyUiActions: (actions: ChatUIAction[]) => void;
  selectObject: (objectType: any, objectId: string) => void;
  onCardAction: (...args: any[]) => any;
  openModal: (...args: any[]) => any;
  archivedInlineModuleIds: Set<string>;
  setArchivedInlineModuleIds: Dispatch<SetStateAction<Set<string>>>;
  isModulePaneVisible: boolean;
  chatPaneModuleId: string | null;
  availableModules: Module[];
  setChatPaneModuleId: (id: string | null) => void;
  setIsModulePaneVisible: (v: boolean) => void;
  setModuleEmbedAnchorMessageId: (id: string | null) => void;
  profile: any;
  token: string | null;
  workspaceId: string;
  queryClient: QueryClient;
  addAssistantMessage: (content: string, conversationId?: string) => void;
  refreshSession: () => Promise<unknown> | unknown;
  onboarding: any;
  isStreaming: boolean;
  recentlySentFilesRef: { current: Map<string, File> };
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  setChatImportCSVFile: (file: File | null) => void;
  appendUserMessage: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  confirmOAuthConsent: (provider: any) => Promise<boolean>;
  shouldShowThinkingTimeline: boolean;
  thinkingTimelineSteps: any;
  pendingModuleSelection: { modules: any[] } | null;
  handleModuleSelection: (...args: any[]) => any;
  setPendingModuleSelection: (v: any) => void;
  error: (Error & { message: string; stack?: string }) | null | undefined;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  // Onboarding-as-transcript: beats render as real assistant messages here, so
  // the driver flags/callbacks (defined in ChatBox) are threaded through.
  onboardingActive: boolean;
  onbIsDemo: boolean;
  lastBeatId: string | null;
  onboardingAdvance: (kind: BeatKind, extra?: Partial<BeatPayload>) => void | Promise<void>;
  onboardingConnect: (kind: 'oauth.google.email' | 'oauth.microsoft.outlook' | 'demo' | 'use-existing') => void | Promise<void>;
  onboardingRunPrompt: (p: string) => void;
  onboardingPersistData: (bundle: Bundle) => void;
  onboardingComplete: () => void | Promise<void>;
  /** Display name of the team workspace; threads into BeatContext.workspaceName. */
  onboardingWorkspaceName?: string | null;
}

export function MessageList({
  voiceMode,
  messages,
  retryLastMessage,
  dismissRetryBubble,
  activeConversation,
  sendMessage,
  runSlashCommand,
  copiedMessageId,
  setCopiedMessageId,
  clickableModuleLabels,
  openWorkspaceModuleByLabel,
  setObjectCardPopover,
  availableModulesRef,
  workspaceModuleCatalogRef,
  applyUiActions,
  selectObject,
  onCardAction,
  openModal,
  archivedInlineModuleIds,
  setArchivedInlineModuleIds,
  isModulePaneVisible,
  chatPaneModuleId,
  availableModules,
  setChatPaneModuleId,
  setIsModulePaneVisible,
  setModuleEmbedAnchorMessageId,
  profile,
  token,
  workspaceId,
  queryClient,
  addAssistantMessage,
  refreshSession,
  onboarding,
  isStreaming,
  recentlySentFilesRef,
  showToast,
  setChatImportCSVFile,
  appendUserMessage,
  confirmOAuthConsent,
  shouldShowThinkingTimeline,
  thinkingTimelineSteps,
  pendingModuleSelection,
  handleModuleSelection,
  setPendingModuleSelection,
  error,
  messagesEndRef,
  onboardingActive,
  onbIsDemo,
  lastBeatId,
  onboardingAdvance,
  onboardingConnect,
  onboardingRunPrompt,
  onboardingPersistData,
  onboardingComplete,
  onboardingWorkspaceName,
}: MessageListProps) {
  // When a new onboarding beat becomes current, scroll its top into view so the
  // user reads from the beginning. Fires after a 300 ms delay so it wins over
  // ChatBox's own scroll-to-bottom logic. paddingBottom on the wrapper keeps the
  // advance button above the chatbox input.
  const currentBeatRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!lastBeatId || !onboardingActive) return;
    const t = setTimeout(() => {
      currentBeatRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(t);
  }, [lastBeatId, onboardingActive]);

  return (
        <div className="chat-messages-inner">
          {voiceMode.isActive && (
            <ErrorBoundary
              onError={(error) => {
                console.error('[VoiceMode] Crash caught by ErrorBoundary:', error);
                voiceMode.exit();
              }}
            >
              <VoiceOverlay
                phase={voiceMode.phase}
                onExit={voiceMode.exit}
                onToggleMic={voiceMode.toggleMic}
                getOutputVolume={voiceMode.getOutputVolume}
                ducking={voiceMode.isDucking}
              />
            </ErrorBoundary>
          )}
          {(voiceMode.isActive ? [] : messages).map((msg) => {
              // The legacy onboarding greeting ("…Pick how you'd like to get started.")
              // was removed in favor of the welcome card. Hide any such row still
              // persisted in older conversations so onboarding never double-greets.
              if (msg.role !== 'user' && typeof msg.content === 'string' && msg.content.includes("Pick how you'd like to get started")) {
                return null
              }
              const isUser = msg.role === 'user'
              const isMsgStreaming = (msg as any).streaming
              const rawContent = isUser ? msg.content : msg.content

              // Retry bubble (layer 3 of the "No response" recovery): the
              // agent produced a truly empty turn. Show an error bubble with
              // the user's original prompt + a Retry button that re-sends it
              // and a Dismiss × that clears the bubble. Never persisted —
              // refresh or a new user message clears it.
              const retryPrompt = (msg as any).retryPrompt as string | undefined
              // Diagnostic tag for the empty-turn case: 'empty-response' (stream
              // + fallback both returned nothing) vs 'connection-lost' (both threw).
              const retryReason = (msg as any).routing as string | undefined
              if (!isUser && msg.deliveryStatus === 'failed' && retryPrompt) {
                return (
                  <div key={msg.id} className="chat-message-row assistant">
                    <div
                      className="chat-bubble-ai chat-bubble-retry"
                      style={{
                        background: '#fef2f2',
                        borderColor: '#fecaca',
                        color: '#7f1d1d',
                      }}
                    >
                      <div style={{ fontSize: 13, marginBottom: 8, opacity: 0.85 }}>
                        No response from the agent on your last message:
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontStyle: 'italic',
                          marginBottom: 12,
                          color: 'var(--color-text-muted)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {retryPrompt}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => { void retryLastMessage() }}
                          style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: '1px solid #991b1b',
                            background: '#991b1b',
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                        >
                          Retry
                        </button>
                        <button
                          type="button"
                          onClick={dismissRetryBubble}
                          aria-label="Dismiss retry"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#7f1d1d',
                            cursor: 'pointer',
                            fontSize: 18,
                            lineHeight: 1,
                            padding: '0 6px',
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {retryReason && (
                        <div style={{ fontSize: 11, marginTop: 8, opacity: 0.6, fontFamily: 'monospace' }}>
                          reason: {retryReason}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              // Skip empty assistant bubbles — including streaming ones with no content yet.
              // The "Thinking..." indicator below provides sufficient loading feedback.
              if (!isUser && !rawContent?.trim() && !msg.inlineModuleId) return null

              // Persisted onboarding BEAT → render the live beat card. Beats are
              // real messages, so the whole tour accumulates into one transcript
              // that survives connect, completion, and reload. Only the latest
              // beat (while onboarding is still active) shows its action button.
              if (!isUser && typeof rawContent === 'string' && rawContent.startsWith(ONBOARDING_BEAT_MARKER)) {
                const beat = parseBeat(rawContent);
                if (!beat) return null;
                const isCurrentBeat = msg.id === lastBeatId && onboardingActive;
                return (
                  <Fragment key={msg.id}>
                    <div
                      className="chat-message-row assistant"
                      ref={isCurrentBeat ? currentBeatRef : null}
                      style={isCurrentBeat ? { scrollMarginTop: '60px', paddingBottom: '20px' } : undefined}
                    >
                      <div className="obr">
                      <OnboardingBeatCard
                        payload={beat}
                        ctx={{
                          isCurrent: isCurrentBeat,
                          token,
                          workspaceId,
                          userEmail: profile?.email,
                          userName: profile?.name,
                          workspaceName: onboardingWorkspaceName,
                          isDemo: onbIsDemo,
                          isStreaming,
                          advance: onboardingAdvance,
                          connect: onboardingConnect,
                          runPrompt: onboardingRunPrompt,
                          runCommand: runSlashCommand,
                          onAssessmentFeedback: (spotOn) => {
                            console.log('[onboarding] assessment feedback:', spotOn ? 'spot-on' : 'missed-things');
                            try { window.dispatchEvent(new CustomEvent('xavior:onboarding-assessment', { detail: { spotOn } })); } catch { /* noop */ }
                          },
                          onScanComplete: onboardingPersistData,
                          onComplete: onboardingComplete,
                          onOpenEvent: (eventId) => onCardAction({ kind: 'events', intent: 'open', data: { eventId } }),
                          onOpenContact: (contactId) => onCardAction({ kind: 'contact', intent: 'open', data: { id: contactId } }),
                          onOpenNote: (noteId) => onCardAction({ kind: 'note', intent: 'open', data: { noteId } }),
                          onOpenPages: () => applyUiActions([{ type: 'module.focus', moduleType: 'pages', conversationId: activeConversation?.id } as ChatUIAction]),
                          onOpenAgents: () => applyUiActions([{ type: 'module.focus', moduleType: 'agents', conversationId: activeConversation?.id } as ChatUIAction]),
                          onOpenTeam: () => { try { window.dispatchEvent(new CustomEvent('openTeamModal', { detail: { tab: 'my-team' } })); } catch { /* noop */ } },
                        }}
                      />
                      </div>
                    </div>
                  </Fragment>
                )
              }

              // Persisted onboarding summary → render the saved cards (survives reload).
              if (!isUser && rawContent?.startsWith(ONBOARDING_SUMMARY_MARKER)) {
                return (
                  <Fragment key={msg.id}>
                    <div className="chat-message-row assistant">
                      <OnboardingSummaryCard
                        content={rawContent}
                        onRunPrompt={(p) => { if (activeConversation?.id) void sendMessage(p, activeConversation.id); }}
                      />
                    </div>
                  </Fragment>
                )
              }

              const isAutoSpeak = (msg as any).routing === 'auto-speak'
              const rowClasses = `chat-message-row ${isUser ? 'user' : 'assistant'}${isAutoSpeak ? ' auto-speak' : ''}${msg.inlineModuleId ? ' module-trigger' : ''}`
              // Inline result cards: an assistant turn may carry a trailing
              // RESULT_CARDS_MARKER block (e.g. matched emails). Strip it from
              // the prose we feed AssistantMarkdown and render the cards after.
              // Tolerates non-marker content → cards = [].
              const { cleanContent: assistantCleanContent, cards: resultCards } = isUser
                ? { cleanContent: rawContent, cards: [] }
                : splitResultCards(rawContent ?? '')
              return (
                <Fragment key={msg.id}>
                  <div className={rowClasses}>
                    {isUser ? (
                      <div className="chat-message">
                        <div className="chat-bubble-user">
                          {rawContent && rawContent.trim() && !/^\[(photo|file)\]$/.test(rawContent.trim()) && (
                            // #1457: render @-mention markers as pills (MentionText
                            // also handles plain text); fall back to markdown RichText
                            // when there are no markers.
                            MENTION_MARKER_RE.test(rawContent)
                              ? <MentionText text={rawContent} />
                              : <RichText blocks={parseBlocks(rawContent)} />
                          )}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="chat-attachments-preview">
                              {msg.attachments.map(att => {
                                // Local blob URLs (in-flight preview) stay as-is; server URLs need the
                                // API base prefix (if relative) plus the JWT in a `_t` query param so
                                // <img>/<a> requests authenticate via the supervisor gateway.
                                const isBlob = att.previewUrl?.startsWith('blob:');
                                const serverUrl = att.url
                                  ? (att.url.startsWith('/') ? `${env.apiBaseUrl}${att.url}` : att.url)
                                  : undefined;
                                const authedServerUrl = serverUrl ? appendAuthToken(serverUrl) : undefined;
                                const imgSrc = isBlob ? att.previewUrl : (authedServerUrl || att.previewUrl);
                                return imgSrc && att.fileType?.startsWith('image/') ? (
                                  <div key={att.assetId || att.filename} className="chat-attachment-image">
                                    <img src={imgSrc} className="chat-attachment-image-preview" alt={att.filename} />
                                  </div>
                                ) : authedServerUrl ? (
                                  <a
                                    key={att.assetId || att.filename}
                                    href={authedServerUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="chat-attachment-chip"
                                    download={att.filename}
                                  >
                                    <svg className="chat-attachment-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14 2 14 8 20 8"/>
                                    </svg>
                                    <span className="chat-attachment-name">{att.filename}</span>
                                  </a>
                                ) : (
                                  <div key={att.assetId || att.filename} className="chat-attachment-chip">
                                    <svg className="chat-attachment-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14 2 14 8 20 8"/>
                                    </svg>
                                    <span className="chat-attachment-name">{att.filename}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="message-actions">
                          <button
                            type="button"
                            className="message-action-btn"
                            title="Copy message"
                            onClick={() => {
                              navigator.clipboard.writeText(msg.content || '');
                              setCopiedMessageId(msg.id);
                              setTimeout(() => setCopiedMessageId((prev) => prev === msg.id ? null : prev), 2000);
                            }}
                          >
                            {copiedMessageId === msg.id ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            )}
                          </button>
                          <button
                            type="button"
                            className="message-action-btn"
                            title="Resend message"
                            onClick={() => { void sendMessage(msg.content || '', undefined, undefined, undefined, { hideUserMessage: true }); }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`chat-message${resultCards.some((c) => c.kind === 'today' || c.kind === 'briefing' || c.kind === 'events' || c.kind === 'inbox_triage') ? ' chat-message--briefing' : ''}`}>
                        <div className={`chat-bubble-ai${isAutoSpeak ? ' auto-speak' : ''}`}>
                          <AssistantMarkdown
                            content={assistantCleanContent}
                            moduleLabels={clickableModuleLabels}
                            trailingInline={!isMsgStreaming ? (
                              <button
                                type="button"
                                className="message-copy-inline-btn"
                                title="Copy message"
                                onClick={() => {
                                  navigator.clipboard.writeText(assistantCleanContent || '');
                                  setCopiedMessageId(msg.id);
                                  setTimeout(() => setCopiedMessageId((prev) => prev === msg.id ? null : prev), 2000);
                                }}
                              >
                                {copiedMessageId === msg.id ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                )}
                              </button>
                            ) : undefined}
                            onModuleLabelClick={(label, occurrenceIndex) => {
                              void openWorkspaceModuleByLabel(label, msg.id, occurrenceIndex);
                            }}
                            onObjectClick={(objectType, objectId) => {
                              if (objectType === 'contact') {
                                setObjectCardPopover({ objectType, objectId });
                                return;
                              }
                              // Find a workspace module that can display this object type
                              const objectTypeToModuleTypes: Record<string, string[]> = {
                                asset: ['drive', 'file_viewer'],
                                note: ['drive', 'note_grid', 'docs'],
                                // 'doc' (ADR 023, #3787): a migrated Drive doc citation
                                // routes to the same module family as 'note'.
                                doc: ['drive', 'note_grid', 'docs'],
                                task: ['task_list', 'kanban'],
                                event: ['event_list', 'calendar', 'timeline'],
                                place: ['place_list', 'place_card', 'place_detail'],
                                stat: ['health'],
                              };
                              const candidateTypes = objectTypeToModuleTypes[objectType] ?? [];
                              // Check conversation modules first, then workspace catalog
                              const convModule = availableModulesRef.current.find(m => candidateTypes.includes(m.type));
                              const wsModule = workspaceModuleCatalogRef.current.find(m => candidateTypes.includes(m.type));
                              const targetModule = convModule || wsModule;
                              if (targetModule) {
                                // Focus the module and dispatch object selection.
                                // module.focus handles both mounted and unmounted modules
                                // (attaching via ensureModulePanelInConversationOnce if needed).
                                // Dispatch selectObject with the original object type so
                                // the correct listener fires (e.g. 'note' → doc editor).
                                applyUiActions([{
                                  type: 'module.focus',
                                  moduleId: targetModule.id,
                                } as ChatUIAction]);
                                // Select after module focus settles — use the same delay
                                // pattern as the existing module.focus objectId handler (line ~2030)
                                setTimeout(() => selectObject(objectType as any, objectId), 500);
                              } else {
                                selectObject(objectType as any, objectId);
                              }
                            }}
                            onEmailClick={(email) => {
                              // Focus the Gmail module (if present) and dispatch a
                              // compose event that EmailPanel listens for.
                              const emailModule = availableModulesRef.current.find(m => m.type === 'email')
                                ?? workspaceModuleCatalogRef.current.find(m => m.type === 'email');
                              if (emailModule) {
                                applyUiActions([{ type: 'module.focus', moduleId: emailModule.id } as ChatUIAction]);
                                setTimeout(() => {
                                  window.dispatchEvent(new CustomEvent('xavior:compose-email', { detail: { to: email } }));
                                }, 500);
                              } else {
                                window.dispatchEvent(new CustomEvent('xavior:compose-email', { detail: { to: email } }));
                              }
                            }}
                          />
                          {resultCards.length > 0 && (
                            <ErrorBoundary
                              onError={(error) => console.error('[ResultCard] render error caught by boundary:', error)}
                            >
                              <ResultCardRenderer cards={resultCards} onCardAction={onCardAction} />
                            </ErrorBoundary>
                          )}
                          {(msg as any).metadata?.isError && (
                            <button
                              type="button"
                              className="chat-message-report-link"
                              onClick={() => openModal({
                                type: 'bug_report',
                                errorInfo: {
                                  message: (msg as any).metadata.errorInfo?.message || 'Chat error',
                                  stack: (msg as any).metadata.errorInfo?.stack || 'No stack trace available',
                                  componentStack: 'ChatStreamingError',
                                }
                              })}
                            >
                              Report this issue
                            </button>
                          )}

                          {/* Thinking and metadata (AI only, when complete) */}
                          {!isMsgStreaming && msg.thinking && (
                            <details className="message-thinking">
                              <summary>View thinking process & tool calls</summary>
                              <pre>{msg.thinking}</pre>
                            </details>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {msg.inlineModuleId && !archivedInlineModuleIds.has(msg.inlineModuleId) && (() => {
                    // Skip inline render when the module pane is already showing this module
                    if (isModulePaneVisible && chatPaneModuleId === msg.inlineModuleId) return null;
                    const inlineMod = availableModules.find((m) => m.id === msg.inlineModuleId);
                    if (!inlineMod) return null;
                    return (
                      <div className="chat-inline-module">
                          <ModuleRenderer
                            module={inlineMod}
                            conversationId={activeConversation?.id}
                            onArchived={() => {
                              setArchivedInlineModuleIds((prev) => new Set(prev).add(inlineMod.id));
                            }}
                            onExpand={() => {
                              setChatPaneModuleId(inlineMod.id);
                              setIsModulePaneVisible(true);
                              setModuleEmbedAnchorMessageId(msg.id);
                            }}
                          />
                      </div>
                    );
                  })()}
                </Fragment>
              )
            })}

            {/* Onboarding action chips — superseded by the beat transcript during
                onboarding; kept only for post-onboarding suggestion/letter states. */}
            {!onboardingActive && (
            <OnboardingActions
              onboardingState={profile?.onboardingState}
              token={token}
              isStreaming={isStreaming}
              onOpenPage={onboarding.handleOpenPage}
              onOAuthPopup={onboarding.handleOAuthPopup}
              onDemoLoad={onboarding.handleDemoLoad}
              onAdvance={async (to) => {
                if (!token) return;
                try {
                  await api.onboarding.advance(token, { to });
                } catch (err) {
                  // An illegal_transition here just means the OAuth callback already
                  // moved the server past this state (race with the optimistic chip
                  // pre-advance). Harmless — resync to the authoritative state.
                  if (!(err instanceof HttpError && err.status === 400)) throw err;
                }
                await refreshSession();
              }}
              onStatusMessage={(text) => {
                addAssistantMessage(text, activeConversation?.id);
              }}
              onSendSuggestion={(msg) => {
                if (activeConversation?.id) void sendMessage(msg, activeConversation.id);
              }}
            />
            )}

            {/* Quick action chips */}
            {!isStreaming && onboarding.quickActions.length > 0 && (
              <QuickActionChips
                actions={onboarding.quickActions}
                onSelect={async (action) => {
                  if (!activeConversation?.id) return;
                  let message = action.message;
                  let sourceJustSynced: 'demo' | 'email' | 'outlook' | null = null;

                  // Contacts CSV import chip — agent emits this when the
                  // user uploads a CSV/VCF. Open ImportCSVModal at the chat
                  // level pre-loaded with the file the user just sent. The
                  // modal POSTs to /bulk/objects which is workspace-scoped;
                  // it doesn't need a Contacts module to be attached to this
                  // conversation. Look the File up from recentlySentFilesRef.
                  if (action.kind === 'contacts.import-csv') {
                    let csvFile: File | null = null;
                    for (const [, f] of recentlySentFilesRef.current) {
                      if (/\.(csv|vcf)$/i.test(f.name)) { csvFile = f; break; }
                    }
                    if (!csvFile) {
                      showToast('Lost the uploaded file — re-attach the CSV and try again.', 'error');
                      return;
                    }
                    setChatImportCSVFile(csvFile);
                    return;
                  }

                  // Chip-driven OAuth: if the chip carries a kind starting with
                  // "oauth.<provider>[.<scope>]", open the consent popup first
                  // and only dispatch the message on a successful grant. This is
                  // the in-chat OAuth flow for onboarding (#1716).
                  if (action.kind && action.kind.startsWith('oauth.')) {
                    if (action.kind.includes('microsoft')) {
                      const { listProviders } = await import('@/lib/api/endpoints/integrations');
                      const providerList = token ? await listProviders(token) : [];
                      const msAvailable = providerList.some((p: { id: string; hasServerCredentials?: boolean }) => p.id === 'microsoft' && p.hasServerCredentials);
                      if (!msAvailable) return;
                    }
                    if (profile?.onboardingState === 'email_pitched' && activeConversation?.id) {
                      const chipLabel = action.label || (action.kind.includes('microsoft') ? 'Connect Outlook' : 'Connect Gmail');
                      appendUserMessage((prev: ChatMessage[]) => [
                        ...prev,
                        { id: `user-${Date.now()}`, role: 'user' as const, content: chipLabel, timestamp: new Date() },
                      ]);
                    }
                    const [, provider, ...rest] = action.kind.split('.');
                    const scopeKey = rest.join('.');
                    // ADR 017 D4: name the capability; the backend resolves the
                    // scope union. No hardcoded frontend scope map (the old map
                    // had drifted — it requested gmail.readonly where the real
                    // grant is gmail.modify).
                    const capability = connectCapabilityFor(provider, scopeKey);
                    // Show the Xavior-side consent interstitial first so
                    // users see the read-only / no-send guarantee before
                    // being handed off to Google. Cancel = no popup.
                    const consentProvider = resolveConsentProvider(action.kind);
                    if (consentProvider) {
                      const proceed = await confirmOAuthConsent(consentProvider);
                      if (!proceed) return;
                    }
                    // Pre-redirect side effects (the page unloads on connect):
                    // dismiss the email welcome so the reload lands in the inbox,
                    // and signal any mounted EmailPanel. FLOW A (onboarding first
                    // connect, no integrationId → the backend keeps the picker).
                    // Same-window redirect; on return the reload refreshes session +
                    // onboarding state and useOnboardingDiscovery takes over (the
                    // backend advances onboarding on the OAuth callback).
                    try { localStorage.setItem('email-welcome-dismissed', '1'); } catch { /* best-effort */ }
                    window.dispatchEvent(new CustomEvent('xavior:email-welcome-dismiss'));
                    try {
                      await connectProvider({
                        provider,
                        authToken: token,
                        capability,
                        conversationId: activeConversation?.id,
                      });
                    } catch { /* fetch failed before redirect — stay on chat to retry */ }
                    return;
                  }

                  // Demo-seed chip: the "Use demo data" alternative to OAuth.
                  // Loads synthetic email fixtures into the user's per-user DB
                  // so the rest of onboarding (analysis, contacts, spending)
                  // has something to talk about.
                  // RESILIENCE: retry up to 3× with backoff (api may be
                  // bouncing during dev hot-reloads). Surface a toast on
                  // final failure instead of silently swallowing.
                  if (action.kind === 'demo.load') {
                    let loaded = false;
                    let lastErr: unknown = null;
                    for (let attempt = 1; attempt <= 3; attempt++) {
                      try {
                        await api.demoSeed.loadDemoSeed(token);
                        loaded = true;
                        break;
                      } catch (err) {
                        lastErr = err;
                        console.warn(`[ChatBox] demo-seed load attempt ${attempt} threw:`, err);
                      }
                      if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 600));
                    }
                    if (!loaded) {
                      console.error('[ChatBox] demo-seed load failed after 3 attempts:', lastErr);
                      showToast('Demo load failed — tap the chip again to retry.', 'error');
                      return;
                    }
                    sourceJustSynced = 'demo';
                    // Skip the Gmail welcome screen so the user lands in
                    // the inbox immediately. Without this they get stuck
                    // on a "Gmail connected — Get started" splash they
                    // didn't ask for and never click. Same shortcut the
                    // real-OAuth flow uses on the calendar_connected URL.
                    try { localStorage.setItem('email-welcome-dismissed', '1'); } catch { /* best-effort */ }
                    // Notify any already-mounted EmailPanel — localStorage
                    // updates don't fire storage events in the same tab,
                    // so the panel needs an explicit signal to drop its
                    // showWelcome state without a page refresh.
                    window.dispatchEvent(new CustomEvent('xavior:email-welcome-dismiss'));
                    // Advance the onboarding state machine — AGENTS.md's
                    // email_pitched section delegates this transition to the
                    // frontend chip handler (OAuth callback handles the
                    // email_connected path server-side; demo path is owned
                    // here). See ADR 010. Guard on profile state so we don't
                    // emit the directive when the user uses the demo chip
                    // outside the onboarding flow.
                    if (token && profile?.onboardingState === 'email_pitched') {
                      try {
                        await api.onboarding.advance(token, { to: 'email_skipped' });
                        // Chain to completed in the same handler — the BOOTSTRAP
                        // email_skipped section's "advance to completed" directive
                        // gets crowded out by the demo-seed briefing flow, so we
                        // own the final transition here. ADR 010.
                        await api.onboarding.advance(token, { to: 'completed' });
                        await refreshSession();
                      } catch (advanceErr) {
                        console.error('[ChatBox] demo.load advance chain failed:', advanceErr);
                      }
                    }
                  }

                  // After a fresh connection (demo OR Gmail), the cached
                  // gmail-status response says "not connected" and the
                  // cached-inbox query has 0 rows. Nuke every gmail-scoped
                  // query so the Email module re-fetches and renders the
                  // freshly-inserted data.
                  if (sourceJustSynced) {
                    queryClient.invalidateQueries({
                      predicate: (q) => {
                        const key = JSON.stringify(q.queryKey);
                        return key.includes('email') || key.includes('gmail') || key.includes('integration');
                      },
                    });
                    if (sourceJustSynced === 'demo') {
                      queryClient.invalidateQueries({ queryKey: ['objects'] });
                      queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
                      queryClient.invalidateQueries({ queryKey: ['modules'] });
                      // Delay the demo-data-loaded event so the EmailPanel has
                      // time to mount after the gmail status query refetches
                      // (connected: false → true). Without this, the event fires
                      // before the panel exists and it never gets the signal.
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
                      }, 1500);
                    }
                  }

                  // "Switch to real Gmail" chip — wipes ALL demo data
                  // (emails, threads, integration row, demo-extracted
                  // contacts) then opens the real Gmail OAuth popup.
                  // No confirmation dialog: the user explicitly tapped
                  // the chip, no surprise. The Email-module header has a
                  // separate confirm-gated path for the same action.
                  let switchedToReal = false;
                  if (action.kind === 'demo.switch-to-real') {
                    try {
                      const result = await api.demoSeed.switchDemoToReal(token);
                      switchedToReal = true;
                      // Nuke all gmail/contact/integration React Query
                      // entries so panels show empty + reflect the
                      // disconnected state immediately.
                      queryClient.invalidateQueries({
                        predicate: (q) => {
                          const k = JSON.stringify(q.queryKey);
                          return k.includes('email') || k.includes('gmail') || k.includes('integration') || k.includes('contact') || k.includes('object');
                        },
                      });
                      window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded'));
                      // Same Xavior-side consent interstitial as the
                      // first-time connect path. User can back out
                      // without ever seeing the Google screen.
                      const proceed = await confirmOAuthConsent('google.email');
                      if (proceed) {
                        // Connect real Gmail via the one connect helper — a
                        // same-window redirect (FLOW A: enroll, no integrationId →
                        // the backend keeps the account picker). On success the page
                        // unloads, so the agent narration can't run here; the reload
                        // reflects the freshly-connected real account.
                        try {
                          await connectProvider({ provider: 'google', authToken: token, capability: 'mail' });
                          return; // redirect in flight — nothing else runs
                        } catch {
                          message = `✓ Demo wiped (${result.messagesRemoved} emails, ${result.contactsRemoved} contacts). Couldn't start the Gmail connect — acknowledge in one sentence and offer to retry.`;
                        }
                      } else {
                        message = `✓ Demo wiped (${result.messagesRemoved} emails, ${result.contactsRemoved} contacts). User cancelled the Gmail connect prompt — acknowledge in one sentence and offer to retry when ready.`;
                      }
                    } catch (err) {
                      console.error('[ChatBox] switch-to-real error:', err);
                      return;
                    }
                  }

                  // Contact extraction chip (post-analysis Step in BOOTSTRAP).
                  // Calls /onboarding/extract-contacts, then folds the result
                  // into the agent's next user message so it can narrate
                  // concrete numbers ("24 people, 8 phones").
                  let contactsExtracted: { twoWayCount: number; created: number; phonesFound: number } | null = null;
                  if (action.kind === 'contacts.extract') {
                    try {
                      contactsExtracted = await api.onboarding.extractContactsRoot(token);
                      // Refetch contacts module so the sidebar count updates
                      queryClient.invalidateQueries({
                        predicate: (q) => JSON.stringify(q.queryKey).includes('contact'),
                      });
                    } catch (err) {
                      console.error('[ChatBox] extract-contacts error:', err);
                    }
                  }
                  if (contactsExtracted) {
                    message = `✓ Contacts extracted. Found ${contactsExtracted.twoWayCount} people you go back-and-forth with — pulled emails for all ${contactsExtracted.created}, phones for ${contactsExtracted.phonesFound}. Narrate this back to me.`;
                  }

                  // Tour chips: open the matching module pane so the
                  // user sees the feature alongside the agent narration.
                  //
                  // #2565 — Onboarding "show calendar" / "show drive" / etc.
                  // chips previously awaited the module-list fetch *and* the
                  // conversation-panel insert *before* dispatching the chat
                  // message, so the agent didn't even start thinking until
                  // both round-trips completed. For tour chips we know
                  // `message` is not rewritten further below (no
                  // sourceJustSynced / contactsExtracted / switchedToReal
                  // mutations apply), so fire `sendMessage` synchronously
                  // and let the module-open work race it in the background.
                  const tourModuleTypes: Record<string, string> = {
                    'calendar.tour': 'event_list',
                    'drive.tour': 'drive',
                    'tasks.tour': 'task_list',
                    'places.tour': 'places',
                    'pages.tour': 'pages',
                  };
                  const isTourChip = action.kind && tourModuleTypes[action.kind];
                  if (isTourChip) {
                    // Fire-and-forget the chat send so latency overlaps with
                    // the module open below. `message` is final at this point
                    // for tour chips, and `opts` is unconditionally undefined
                    // (none of sourceJustSynced/contactsExtracted/switchedToReal
                    // are set on this code path).
                    void sendMessage(message, activeConversation.id, undefined, undefined, undefined);

                    // #2565 — Open the pane synchronously from the in-memory
                    // module catalog (refreshed on mount + on module changes)
                    // so the visual pane open isn't gated on a network
                    // round-trip. The persistence call below runs in the
                    // background — failure is a 409 (panel already exists)
                    // or transient that the next mount recovers from.
                    const targetType = tourModuleTypes[action.kind!];
                    const findMod = (mods: WorkspaceModule[]) => mods.find((m) => m.type === targetType);
                    const openPane = (mod: WorkspaceModule) => {
                      setChatPaneModuleId(mod.id);
                      setIsModulePaneVisible(true);
                      void api.workspaces.createConversationPanel(token, workspaceId, activeConversation.id, {
                        workspaceModuleId: mod.id,
                        panelKey: `tour-${mod.type}`,
                      }).catch(() => { /* 409 = already exists, fine */ });
                    };

                    const cached = findMod(workspaceModuleCatalogRef.current);
                    if (cached) {
                      openPane(cached);
                    } else {
                      // Cache miss (rare during onboarding — catalog is
                      // populated on mount). Fetch once, then open.
                      try {
                        const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {});
                        const mod = findMod(modules);
                        if (mod) openPane(mod);
                      } catch { /* best effort */ }
                    }
                    // We already sent the message above — skip the trailing
                    // sendMessage at the bottom of this handler.
                    return;
                  }

                  if (action.kind === 'tour.end') {
                    setChatPaneModuleId(null);
                    setIsModulePaneVisible(false);
                  }

                  // Daily-briefing opt-in chip (emitted by the xavior-letter
                  // skill step 5). Flips the per-user daily_briefing_enabled
                  // flag server-side, then forwards the message to the agent
                  // so it can acknowledge. Errors are non-fatal — the agent
                  // still replies, the flag just stays unset.
                  if (action.kind === 'briefing.enable' || action.kind === 'briefing.disable') {
                    const enabled = action.kind === 'briefing.enable';
                    try {
                      await api.onboarding.setBriefingPreference(token, enabled);
                    } catch (err) {
                      console.warn('[ChatBox] briefing-preference failed:', err);
                    }
                  }

                  // After switch-to-real, also need to update the message
                  // with summary IF the OAuth completed and synced data.
                  if (switchedToReal) { /* message already set above */ }

                  // After a successful sync (real Gmail OR demo), fetch the
                  // email analysis and fold it into the outbound user message
                  // so the agent can narrate concrete numbers instead of
                  // guessing. We embed the breakdown in parentheses; BOOTSTRAP
                  // instructs the agent to read it aloud naturally.
                  //
                  // CRITICAL: even if email-summary fails, we MUST rewrite
                  // the message. Otherwise it stays as "Use demo data" — a
                  // phrase BOOTSTRAP's "re-loading demo on demand" path
                  // matches and responds to by re-emitting the demo chip +
                  // a "Loading demo data — give it a few seconds." filler,
                  // which the user can then click again, looping forever.
                  // The "Narrate this back to me" suffix routes the agent
                  // to BOOTSTRAP's post-sync narration path either way.
                  if (sourceJustSynced) {
                    let summaryLine = '';
                    try {
                      const summary = await api.onboarding.getEmailSummary(token);
                      const catLine = summary.byCategory.map((c) => `${c.label} ${c.count}`).join(', ');
                      const topThree = summary.topSenders.slice(0, 3).map((s) => `${s.name} (${s.count})`).join(', ');
                      const windowDays = summary.windowDays || 60;
                      summaryLine = `Analysis: ${summary.totalEmails} emails in the last ${windowDays} days — ${catLine}. Top senders: ${topThree}.`;
                    } catch (err) { console.warn('[ChatBox] email-summary threw:', err); }
                    const sourceLabel = sourceJustSynced === 'demo' ? 'demo data' : sourceJustSynced === 'outlook' ? 'Outlook' : 'Gmail';
                    message = summaryLine
                      ? `✓ Connected (${sourceLabel}). ${summaryLine} Narrate this back to me as the next step of onboarding.`
                      : `✓ Connected (${sourceLabel}). Narrate this back to me as the next step of onboarding.`;
                  }

                  // Instant module focus for Gmail-related actions (don't wait for agent)
                  if (/connect.*email|let.*s do it/i.test(action.message)) {
                    try {
                      const modules = await api.workspaces.listWorkspaceModules(token, workspaceId, {});
                      const emailModule = modules.find((m: any) => m.type === 'email');
                      if (emailModule) {
                        setChatPaneModuleId(emailModule.id);
                        setIsModulePaneVisible(true);
                      }
                    } catch { /* best effort */ }
                  }
                  // After sync, contact extraction, or switch-to-real,
                  // we inject the result into the user message so the
                  // agent has real numbers to narrate. Show the chip's
                  // label as the visible bubble. Fall back to action.label
                  // because BOOTSTRAP-emitted chips (e.g. demo.load) only
                  // carry {label, kind} — without this fallback the long
                  // analysis blob leaks into the user bubble.
                  const opts = (sourceJustSynced || contactsExtracted || switchedToReal)
                    ? { displayContent: action.message ?? action.label }
                    : undefined;
                  void sendMessage(message, activeConversation.id, undefined, undefined, opts);
                }}
              />
            )}

            {/* Live streaming activity timeline — hidden once text starts streaming */}
            {shouldShowThinkingTimeline && !voiceMode.isActive && (
              <ThinkingTimeline steps={thinkingTimelineSteps} />
            )}

            {/* Onboarding discovery ephemeral status */}
            {onboarding.discoveryStatus && !shouldShowThinkingTimeline && !voiceMode.isActive && (
              <div className="streaming-status-indicator">
                <div className="thinking-timeline">
                  <div className="thinking-step thinking-step--text is-active">
                    <AnimatedThinkingLabel text={onboarding.discoveryStatus} />
                  </div>
                </div>
              </div>
            )}

            {/* Module selection buttons */}
            {pendingModuleSelection && (
              <ModuleSelectionButtons
                modules={pendingModuleSelection.modules}
                onSelect={handleModuleSelection}
                onCancel={() => setPendingModuleSelection(null)}
              />
            )}

            {/* Inline error card — rendered inside the chat stream so the
                max-iterations / network / agent error appears as a
                system-error message rather than as a free-floating bar
                wedged between the last message and the composer (#1480). */}
            {error && (
              <ChatInlineError
                error={error}
                onRetry={retryLastMessage}
                onReport={(err) => openModal({
                  type: 'bug_report',
                  errorInfo: {
                    message: err.message,
                    stack: err.stack,
                    componentStack: 'ChatBox',
                  }
                })}
              />
            )}

          <div ref={messagesEndRef} />
        </div>
  );
}
